import type { Dictionary, SortableArrayItem } from '@flemingmetrics/api-client';
import { isBlank } from '@/utils/strings';

export function mergeWithoutDuplicates<T>(...arrays: T[][]) {
  return [...new Set(([] as T[]).concat(...arrays))];
}

export function mergeWithoutDuplicatesByKey<T extends SortableArrayItem>(
  arr1: T[],
  arr2: T[],
  key = 'id'
) {
  return arr1.concat(
    arr2.filter((item) => !includesValue(arr1, key, item[key]))
  );
}

export function filterByValue<T>(arr: T[], value: T) {
  return arr.filter((entry) => entry !== value);
}

export function filterByValues<T>(arr: T[], values: T[]) {
  return arr.filter((entry) => !values.includes(entry));
}

export function filterByKey<T extends SortableArrayItem, K extends keyof T>(
  arr: T[],
  key: keyof T,
  val: T[K]
) {
  return arr.filter((entry) => entry[key] !== val);
}

export function sumByKey<T extends SortableArrayItem>(arr: T[], key: keyof T) {
  return arr.reduce((acc, entry) => acc + entry[key], 0);
}

export function hasBlanks(arr: string[]) {
  return arr.some(isBlank);
}

export function mapToKey<T, K extends keyof T>(arr: T[], key: K): T[K][] {
  return arr.map((item) => item[key]);
}

export function mapToId<T extends { id: number }>(arr: T[]): number[] {
  return mapToKey(arr, 'id');
}

export function sortByKey<T extends SortableArrayItem>(
  arr: T[],
  key: string,
  order = 1
) {
  return arr.sort((a, b) => {
    let obj1 = order === 1 ? a : b;
    let obj2 = order === 1 ? b : a;
    if (obj1[key] > obj2[key]) return -1;
    if (obj2[key] > obj1[key]) return 1;
    return 0;
  });
}

export function sortAlphabeticalByKey<T extends SortableArrayItem>(
  arr: T[],
  key = 'name',
  order = 1,
  caseSensitive = false
): T[] {
  return sortAlphabeticalByObjectValue(
    arr,
    (item) => item[key],
    order,
    caseSensitive
  );
}

export function sortAlphabeticalByObjectValue<T extends SortableArrayItem>(
  arr: T[],
  getValue: (item: T) => string,
  order = 1,
  caseSensitive = false
): T[] {
  const sensitivity = caseSensitive ? 'base' : 'variant';
  const result = [...arr].sort((a, b) =>
    getValue(a).localeCompare(getValue(b), [...navigator.languages], {
      sensitivity,
    })
  );
  if (order === -1) result.reverse();
  return result;
}

export function sortByCreatedAt<T extends Dictionary<any>>(
  arr: T[],
  order = 1
) {
  return sortByKey(arr, 'createdAt', order);
}

export function sortByUpdatedAt<T extends Dictionary<any>>(
  arr: T[],
  order = 1
) {
  return sortByKey(arr, 'updatedAt', order);
}

export function includesValue(
  arr: Record<string, any>[],
  key: string,
  value: any
) {
  return arr.some((obj) => obj[key] === value);
}

export function findByKey<T extends Dictionary, K extends keyof T>(
  arr: T[],
  key: K,
  value: T[K]
): T | undefined {
  return arr.find((item) => item[key] === value);
}

export function findById<T extends { id: number }>(
  arr: T[],
  id: number
): T | undefined {
  return findByKey(arr, 'id', id);
}

export function findByUUID<T extends { uuid: string }>(
  arr: T[],
  uuid: string
): T | undefined {
  return findByKey(arr, 'uuid', uuid);
}

export function findByValue<T extends { value: unknown }>(
  arr: T[],
  value: T['value']
): T | undefined {
  return findByKey(arr, 'value', value);
}

export function findIndexByKey<T extends Dictionary, K extends keyof T>(
  arr: T[],
  key: K,
  value: T[K]
): number {
  return arr.findIndex((item) => item[key] === value);
}

export function findIndexById<T extends { id: number }>(
  arr: T[],
  id: number
): number {
  return findIndexByKey(arr, 'id', id);
}

export function findIndexByUUID<T extends { uuid: string }>(
  arr: T[],
  uuid: string
): number {
  return findIndexByKey(arr, 'uuid', uuid);
}

export function findIndexByValue<T extends { value: unknown }>(
  arr: T[],
  value: T['value']
): number {
  return findIndexByKey(arr, 'value', value);
}

export function addToIdArray<T>(arr: T[] | undefined, id: T) {
  return arr ? mergeWithoutDuplicates<T>(arr, [id]) : [id];
}
